import { useEffect, useId, useRef, useSyncExternalStore } from 'react';

import CaptchaBox from '@/containers/CaptchaBox';

import type { WechatQRController } from './controller';
import { wechatCopy, wechatNotice } from './copy';
import styles from './index.module.scss';

type Props = {
  readonly controller: WechatQRController;
  readonly isEmbedded?: boolean;
  readonly isMobile?: boolean;
  readonly isBinding?: boolean;
  readonly locale?: string;
  readonly onOtherMethod: () => void;
};

const QrCode = ({
  controller,
  isEmbedded = false,
  isMobile = false,
  isBinding = false,
  locale = 'zh-CN',
  onOtherMethod,
}: Props) => {
  const view = useSyncExternalStore(controller.subscribe, controller.getView, controller.getView);
  const copy = wechatCopy(locale);
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    title.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (!isMobile) {
      controller.start();
    }
    return () => {
      controller.stop();
    };
  }, [controller, isMobile]);
  const other = () => {
    controller.cancel();
    onOtherMethod();
  };
  return (
    <section className={styles.panel} aria-labelledby={titleId}>
      {!isEmbedded && (
        <div className={styles.brand}>
          <span aria-hidden="true" />
          VibeHub
        </div>
      )}
      <h1 ref={title} id={titleId} tabIndex={-1}>
        {isBinding ? copy.bindTitle : copy.title}
      </h1>
      <p className={styles.description}>{isMobile ? copy.mobile : copy.description}</p>
      <button className={styles.secondary} type="button" onClick={other}>
        {copy.other}
      </button>
      {!isMobile && (
        <>
          <div className={styles.qrSlot}>
            {view.phase === 'qr' && <div ref={controller.attach} className={styles.frame} />}
            {view.phase === 'preparing' && <p role="status">{copy.prepare}</p>}
            {view.phase === 'window' && <p role="status">{copy.windowPending}</p>}
            {view.phase === 'unavailable' && <p role="alert">{copy.unavailable}</p>}
          </div>
          {view.notice && view.phase !== 'unavailable' && (
            <p className={styles.error} role="alert">
              {wechatNotice(view.notice, locale)}
            </p>
          )}
          {view.phase === 'qr' && <p className={styles.hint}>{copy.help}</p>}
          <CaptchaBox />
          {['qr', 'window'].includes(view.phase) && (
            <button
              className={styles.secondary}
              type="button"
              onClick={() => {
                controller.openWindow();
              }}
            >
              {copy.window}
            </button>
          )}
          <button
            className={styles.secondary}
            type="button"
            disabled={view.busy}
            onClick={() => {
              void controller.refresh();
            }}
          >
            {view.phase === 'unavailable' ? copy.retry : copy.refresh}
          </button>
        </>
      )}
    </section>
  );
};
export default QrCode;
